import { Fragment, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, type ApiError } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { titledErrorMessage } from '../api/problems'
import { ADMIN_CONNECTORS_QUERY_KEY } from '../api/categories'
import {
  UPSTREAMS_QUERY_KEY,
  fmtTime,
  oauthKey,
  reviewKey,
  reviewStateLabel,
  upstreamKey,
  type OAuthDiff,
  type OAuthRegistration,
  type OAuthVerifyResult,
  type ReviewListing,
  type ReviewTool,
  type SyncSummary,
  type ToolSnapshot,
  type Upstream,
  type UpstreamWrite,
} from '../api/mcpUpstreams'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { ExposureEditor } from '../components/ExposureEditor'
import './screens.css'

function show(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'string') return v
  return JSON.stringify(v, null, 2)
}

// ── Sync ─────────────────────────────────────────────────────────────────────

function SyncSummaryView({ s }: { s: SyncSummary }) {
  const line = (label: string, names: string[]) =>
    names.length > 0 ? (
      <li>
        {label} ({names.length}): <span className="scr-mono">{names.join(', ')}</span>
      </li>
    ) : null
  return (
    <div className="scr-ok" aria-label="Sync result">
      Synced {s.tool_count} tools.
      <ul>
        {line('New, pending review', s.new)}
        {line('Held description changes', s.changed)}
        {line('Suspended schema changes', s.suspended)}
        {line('Removed (deactivated)', s.removed)}
        {line('Unchanged', s.unchanged)}
        {s.errors.map((e) => (
          <li key={e.upstream_name} className="scr-err">
            {e.upstream_name}: {e.error}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── ToS review ───────────────────────────────────────────────────────────────

function TosPanel({ upstream }: { upstream: Upstream }) {
  const qc = useQueryClient()
  const [url, setUrl] = useState(upstream.tos_url ?? '')
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const save = useMutation({
    mutationFn: () =>
      apiFetch<Upstream>(`/admin/connectors/mcp/${encodeURIComponent(upstream.slug)}/tos-review`, {
        method: 'PUT',
        body: JSON.stringify({ tos_url: url.trim(), note: note.trim() }),
      }),
    onSuccess: () => {
      setNote('')
      qc.invalidateQueries({ queryKey: upstreamKey(upstream.slug) })
      qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY })
    },
  })
  useWriteForbiddenGuard(save.error)

  return (
    <div className="scr-panel">
      <h3>Vendor ToS review</h3>
      <p className="scr-note">
        Required before this upstream can go live. The URL is recorded, never fetched.
      </p>
      <p>
        {upstream.tos_reviewed_at ? (
          <>
            Reviewed {fmtTime(upstream.tos_reviewed_at)} by{' '}
            <span className="scr-mono">{upstream.tos_reviewed_by ?? '—'}</span>
            {upstream.tos_note ? ` — ${upstream.tos_note}` : ''}
          </>
        ) : (
          <strong>Not recorded.</strong>
        )}
      </p>
      <div className="scr-form">
        <label>
          Terms URL
          <input
            className="scr-input"
            value={url}
            placeholder="https://vendor.com/terms"
            onChange={(e) => setUrl(e.target.value)}
          />
        </label>
        <label>
          Review note
          <input
            className="scr-input"
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>
      <div className="scr-panel-actions">
        <button
          type="button"
          className="screen-btn"
          disabled={!url.trim().startsWith('https://') || !note.trim() || save.isPending}
          onClick={() => setConfirming(true)}
        >
          Record ToS review
        </button>
        {save.isSuccess && <span className="scr-ok">ToS review recorded.</span>}
      </div>
      {save.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(save.error)}
        </p>
      )}
      {confirming && (
        <ConfirmDialog
          label="Confirm ToS review"
          confirmText="Record"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false)
            save.mutate()
          }}
        >
          <p>
            Record that you reviewed the vendor terms at <span className="scr-mono">{url.trim()}</span>{' '}
            for <strong>{upstream.slug}</strong>, as yourself?
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

// ── Credentials (PATCH) ──────────────────────────────────────────────────────

function CredentialsPanel({ upstream }: { upstream: Upstream }) {
  const qc = useQueryClient()
  const oauth = upstream.auth_scheme === 'oauth2'
  const [scheme, setScheme] = useState<'api_key' | 'bearer'>(
    upstream.auth_scheme === 'api_key' ? 'api_key' : 'bearer',
  )
  const [header, setHeader] = useState(upstream.api_key_header ?? '')
  const [credential, setCredential] = useState('')
  const [pending, setPending] = useState<{ body: Record<string, unknown>; summary: string } | null>(
    null,
  )
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<UpstreamWrite>(
        `/admin/connectors/mcp/${encodeURIComponent(upstream.slug)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      setCredential('')
      qc.invalidateQueries({ queryKey: upstreamKey(upstream.slug) })
      qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY })
    },
  })
  useWriteForbiddenGuard(patch.error)

  const schemeChanged = !oauth && scheme !== upstream.auth_scheme
  const headerChanged = !oauth && scheme === 'api_key' && header.trim() !== (upstream.api_key_header ?? '')

  const saveAuth = () => {
    const body: Record<string, unknown> = { auth_scheme: scheme }
    if (scheme === 'api_key') body.api_key_header = header.trim()
    if (credential) body.sync_credential = credential
    setPending({
      body,
      summary: schemeChanged
        ? `change auth scheme ${upstream.auth_scheme} → ${scheme}. Every connected account of this connector is marked needs_reconnect`
        : 'update the auth settings',
    })
  }

  return (
    <div className="scr-panel">
      <h3>Credentials</h3>
      <p className="scr-note">
        The sync credential is used only for tools/list. It is write-only: it is never shown.
        Currently: <strong>{upstream.has_sync_credential ? 'set' : 'not set'}</strong>.
        {oauth && ' MCP OAuth upstreams cannot change scheme here — register again instead.'}
      </p>
      <div className="scr-form">
        {!oauth && (
          <label>
            Auth scheme
            <select value={scheme} onChange={(e) => setScheme(e.target.value as 'api_key' | 'bearer')}>
              <option value="api_key">api_key</option>
              <option value="bearer">bearer</option>
            </select>
          </label>
        )}
        {!oauth && scheme === 'api_key' && (
          <label>
            API key header
            <input className="scr-input" value={header} onChange={(e) => setHeader(e.target.value)} />
          </label>
        )}
        <label>
          New sync credential
          <input
            className="scr-input"
            type="password"
            autoComplete="off"
            value={credential}
            onChange={(e) => setCredential(e.target.value)}
          />
        </label>
      </div>
      <div className="scr-panel-actions">
        {!oauth && (
          <button
            type="button"
            className="screen-btn"
            disabled={
              patch.isPending ||
              (!schemeChanged && !headerChanged && !credential) ||
              (scheme === 'api_key' && !header.trim())
            }
            onClick={saveAuth}
          >
            Save auth settings
          </button>
        )}
        {oauth && (
          <button
            type="button"
            className="screen-btn"
            disabled={patch.isPending || !credential}
            onClick={() =>
              setPending({ body: { sync_credential: credential }, summary: 'replace the sync credential' })
            }
          >
            Set sync credential
          </button>
        )}
        {upstream.has_sync_credential && (
          <button
            type="button"
            className="screen-btn"
            disabled={patch.isPending}
            onClick={() =>
              setPending({ body: { sync_credential: null }, summary: 'CLEAR the stored sync credential' })
            }
          >
            Clear sync credential
          </button>
        )}
        {patch.isSuccess && (
          <span className="scr-ok">
            Saved.
            {patch.data?.accounts_marked_needs_reconnect
              ? ` ${patch.data.accounts_marked_needs_reconnect} account(s) marked needs_reconnect.`
              : ''}
          </span>
        )}
      </div>
      {patch.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(patch.error)}
        </p>
      )}
      {pending && (
        <ConfirmDialog
          label="Confirm credential change"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            patch.mutate(pending.body)
            setPending(null)
          }}
        >
          <p>
            <strong>{upstream.slug}</strong>: {pending.summary}?
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

// ── MCP OAuth ────────────────────────────────────────────────────────────────

function OAuthDiffTable({ diff }: { diff: OAuthDiff }) {
  return (
    <table className="scr-table" aria-label="OAuth metadata change">
      <thead>
        <tr>
          <th>Field</th>
          <th>Frozen (serving)</th>
          <th>Observed (held)</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(diff).map(([k, v]) => (
          <tr key={k}>
            <td className="scr-mono">{k}</td>
            <td className="scr-mono">{show(v.frozen)}</td>
            <td className="scr-mono">{show(v.observed)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function OAuthPanel({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const [confirmHash, setConfirmHash] = useState<string | null>(null)
  const reg = useQuery<OAuthRegistration>({
    queryKey: oauthKey(slug),
    queryFn: () => apiFetch(`/admin/connectors/mcp/${encodeURIComponent(slug)}/oauth`),
  })
  useForbiddenGuard(reg.error)

  const verify = useMutation({
    mutationFn: () =>
      apiFetch<OAuthVerifyResult>(`/admin/connectors/mcp/${encodeURIComponent(slug)}/oauth/verify`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: oauthKey(slug) }),
  })
  useWriteForbiddenGuard(verify.error)

  const approve = useMutation({
    mutationFn: (metadata_hash: string) =>
      apiFetch<{ status: string; accounts_marked_needs_reconnect?: number }>(
        `/admin/connectors/mcp/${encodeURIComponent(slug)}/oauth/approve`,
        { method: 'POST', body: JSON.stringify({ metadata_hash }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: oauthKey(slug) }),
  })
  useWriteForbiddenGuard(approve.error)

  const r = reg.data
  return (
    <div className="scr-panel">
      <h3>MCP OAuth</h3>
      {reg.isError && (reg.error as unknown as ApiError).status !== 403 && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(reg.error)}
        </p>
      )}
      {r && (
        <>
          <dl className="scr-dl">
            <dt>Registration</dt>
            <dd>{r.registration_method ?? '—'}</dd>
            <dt>Issuer</dt>
            <dd className="scr-mono">{r.issuer ?? '—'}</dd>
            <dt>Resource</dt>
            <dd className="scr-mono">{r.resource ?? '—'}</dd>
            <dt>Authorization endpoint</dt>
            <dd className="scr-mono">{r.authorization_endpoint ?? '—'}</dd>
            <dt>Token endpoint</dt>
            <dd className="scr-mono">{r.token_endpoint ?? '—'}</dd>
            <dt>Registration endpoint</dt>
            <dd className="scr-mono">{r.registration_endpoint ?? '—'}</dd>
            <dt>Scopes</dt>
            <dd className="scr-mono">{r.scopes.join(' ') || '—'}</dd>
            <dt>Metadata hash</dt>
            <dd className="scr-mono">{r.metadata_hash ?? '—'}</dd>
            <dt>Verified</dt>
            <dd>{fmtTime(r.verified_at)}</dd>
          </dl>
          {r.pending && (
            <div className="scr-exposure" aria-label="Held OAuth change">
              <p>
                <span className="scr-badge scr-badge--changed">Endpoint change held</span>{' '}
                observed {fmtTime(r.pending.observed_at)} — hash{' '}
                <span className="scr-mono">{r.pending.metadata_hash}</span>. The frozen endpoints
                keep serving until approved.
              </p>
              <OAuthDiffTable diff={r.pending.diff} />
              <div className="scr-actions">
                <button
                  type="button"
                  className="screen-btn screen-btn-primary"
                  disabled={approve.isPending}
                  onClick={() => setConfirmHash(r.pending!.metadata_hash)}
                >
                  Approve held change
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <div className="scr-panel-actions">
        <button
          type="button"
          className="screen-btn"
          disabled={verify.isPending || !r}
          onClick={() => verify.mutate()}
        >
          {verify.isPending ? 'Verifying…' : 'Re-verify OAuth metadata'}
        </button>
        {verify.isSuccess && verify.data && (
          <span className="scr-ok">
            {verify.data.status === 'unchanged'
              ? 'Unchanged — check recorded.'
              : 'Changed — held for approval below.'}
          </span>
        )}
        {approve.isSuccess && (
          <span className="scr-ok">
            Approved and frozen.
            {approve.data?.accounts_marked_needs_reconnect
              ? ` ${approve.data.accounts_marked_needs_reconnect} account(s) must reconnect.`
              : ''}
          </span>
        )}
      </div>
      {verify.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(verify.error)}
        </p>
      )}
      {approve.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(approve.error)}
        </p>
      )}
      {confirmHash && (
        <ConfirmDialog
          label="Confirm OAuth approval"
          confirmText="Approve"
          onCancel={() => setConfirmHash(null)}
          onConfirm={() => {
            approve.mutate(confirmHash)
            setConfirmHash(null)
          }}
        >
          <p>
            Freeze the observed OAuth metadata (hash <span className="scr-mono">{confirmHash.slice(0, 12)}…</span>)?
            A different authorization server re-registers the platform client and marks every
            connected account needs_reconnect.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

// ── Review gate ──────────────────────────────────────────────────────────────

const SNAP_FIELDS: (keyof ToolSnapshot)[] = ['title', 'description', 'annotations', 'input_schema']

function ToolDiff({ tool }: { tool: ReviewTool }) {
  return (
    <div className="scr-diff" aria-label={`Approved vs observed for ${tool.name}`}>
      <div>
        <strong>Approved (serving)</strong>
        {tool.approved ? (
          SNAP_FIELDS.map((f) => (
            <div key={f}>
              <div className="scr-muted">{f}</div>
              <pre className="scr-pre">{show(tool.approved?.[f])}</pre>
            </div>
          ))
        ) : (
          <p className="scr-muted">Never approved.</p>
        )}
      </div>
      <div>
        <strong>Observed (upstream, verbatim)</strong>
        {SNAP_FIELDS.map((f) => (
          <div key={f}>
            <div className="scr-muted">
              {f}
              {tool.diff[f as keyof ReviewTool['diff']] ? ' — changed' : ''}
            </div>
            <pre className="scr-pre">{show(tool.observed?.[f])}</pre>
          </div>
        ))}
      </div>
    </div>
  )
}

function changedFields(t: ReviewTool): string {
  if (t.diff.new) return 'new'
  const f = (['description', 'title', 'annotations', 'input_schema'] as const).filter((k) => t.diff[k])
  return f.length ? f.join(', ') : '—'
}

function ReviewPanel({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState<string | null>(null)
  const [pending, setPending] = useState<{ action: 'approve' | 'reject'; tool: ReviewTool } | null>(
    null,
  )
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const listing = useQuery<ReviewListing>({
    queryKey: reviewKey(slug),
    queryFn: () => apiFetch(`/admin/connectors/mcp/${encodeURIComponent(slug)}/review`),
  })
  useForbiddenGuard(listing.error)

  const review = useMutation({
    mutationFn: (body: { approve?: string[]; reject?: string[] }) =>
      apiFetch(`/admin/connectors/mcp/${encodeURIComponent(slug)}/review`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_d, body) => {
      setOkMsg(body.approve ? `Approved ${body.approve.join(', ')}.` : `Rejected ${body.reject?.join(', ')}.`)
      qc.invalidateQueries({ queryKey: reviewKey(slug) })
      qc.invalidateQueries({ queryKey: ADMIN_CONNECTORS_QUERY_KEY })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(review.error)

  const tools = listing.data?.tools ?? []
  return (
    <div className="scr-panel">
      <h3>Tool review</h3>
      <p className="scr-note">
        Approve copies the observed version to approved and serves it. Reject records the
        observed version as rejected; rejecting an approved tool withdraws its approval. Flags
        are a reviewer aid, not a filter.
      </p>
      {listing.isError && (listing.error as unknown as ApiError).status !== 403 && (
        <p className="scr-err" role="alert">
          Failed to load the review listing.
        </p>
      )}
      {review.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(review.error)}
        </p>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}
      <table className="scr-table" aria-label="Tools under review">
        <thead>
          <tr>
            <th>Tool</th>
            <th>State</th>
            <th>Changed</th>
            <th>Flags</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((t) => {
            const st = reviewStateLabel(t)
            const canApprove = t.state === 'pending' || t.state === 'changed'
            const canReject = t.state !== 'removed'
            return (
              <Fragment key={t.name}>
                <tr className={t.state === 'removed' ? 'scr-row--muted' : undefined}>
                  <td>
                    <span className="scr-mono">{t.name}</span>
                    {t.upstream_name && t.upstream_name !== t.name && (
                      <div className="scr-muted scr-mono">upstream: {t.upstream_name}</div>
                    )}
                    {t.sync_error && <div className="scr-err">{t.sync_error}</div>}
                  </td>
                  <td>
                    <span className={`scr-badge scr-badge--${st.badge}`}>{st.label}</span>
                    {t.rejected && <span className="scr-chip">observed version rejected</span>}
                  </td>
                  <td>{changedFields(t)}</td>
                  <td>
                    <span className="scr-chips">
                      {t.flags.length === 0 && <span className="scr-muted">—</span>}
                      {t.flags.map((f) => (
                        <span key={f} className="scr-chip">
                          {f}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td>
                    <span className="scr-actions">
                      <button
                        type="button"
                        className="screen-btn"
                        aria-expanded={open === t.name}
                        aria-label={`Diff ${t.name}`}
                        onClick={() => setOpen((o) => (o === t.name ? null : t.name))}
                      >
                        {open === t.name ? 'Hide diff' : 'Diff'}
                      </button>
                      {canApprove && (
                        <button
                          type="button"
                          className="screen-btn"
                          aria-label={`Approve ${t.name}`}
                          disabled={review.isPending}
                          onClick={() => setPending({ action: 'approve', tool: t })}
                        >
                          Approve
                        </button>
                      )}
                      {canReject && (
                        <button
                          type="button"
                          className="screen-btn"
                          aria-label={`Reject ${t.name}`}
                          disabled={review.isPending}
                          onClick={() => setPending({ action: 'reject', tool: t })}
                        >
                          Reject
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
                {open === t.name && (
                  <tr>
                    <td colSpan={5}>
                      <ToolDiff tool={t} />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          {!listing.isLoading && tools.length === 0 && (
            <tr>
              <td colSpan={5} className="ws-empty">
                No tools yet — run a sync.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {pending && (
        <ConfirmDialog
          label={`Confirm ${pending.action}`}
          confirmText={pending.action === 'approve' ? 'Approve' : 'Reject'}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            setOkMsg(null)
            review.mutate(
              pending.action === 'approve'
                ? { approve: [pending.tool.name] }
                : { reject: [pending.tool.name] },
            )
            setPending(null)
          }}
        >
          <p>
            {pending.action === 'approve' ? (
              <>
                Approve <strong className="scr-mono">{pending.tool.name}</strong>? Its observed
                upstream text and schema become what agents are served.
              </>
            ) : pending.tool.state === 'approved' ? (
              <>
                Withdraw approval of <strong className="scr-mono">{pending.tool.name}</strong>? It
                stops being served.
              </>
            ) : (
              <>
                Reject the observed version of{' '}
                <strong className="scr-mono">{pending.tool.name}</strong>?
              </>
            )}
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

// ── View ─────────────────────────────────────────────────────────────────────

export function McpUpstreamDetailView() {
  const { slug = '' } = useParams()
  const qc = useQueryClient()
  const [confirmSync, setConfirmSync] = useState(false)

  const query = useQuery<Upstream>({
    queryKey: upstreamKey(slug),
    queryFn: () => apiFetch(`/admin/connectors/mcp/${encodeURIComponent(slug)}`),
  })
  useForbiddenGuard(query.error)

  const sync = useMutation({
    mutationFn: () =>
      apiFetch<SyncSummary>(`/admin/connectors/mcp/${encodeURIComponent(slug)}/sync`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: upstreamKey(slug) })
      qc.invalidateQueries({ queryKey: reviewKey(slug) })
      qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ADMIN_CONNECTORS_QUERY_KEY })
    },
  })
  useWriteForbiddenGuard(sync.error)

  const u = query.data

  return (
    <section className="scr" aria-labelledby="mcpud-heading">
      <p>
        <Link to="/mcp-upstreams">← MCP Upstreams</Link>
      </p>
      <div className="screen-head">
        <h2 id="mcpud-heading">{u ? u.display_name || u.connector_name : slug}</h2>
        {u && (
          <span className="scr-actions">
            {u.resync_suggested_at && (
              <span className="scr-badge scr-badge--changed" title={fmtTime(u.resync_suggested_at)}>
                re-sync suggested
              </span>
            )}
            <button
              type="button"
              className="screen-btn screen-btn-primary"
              disabled={sync.isPending}
              onClick={() => setConfirmSync(true)}
            >
              {sync.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          </span>
        )}
      </div>

      {query.isError && (query.error as unknown as ApiError).status !== 403 && (
        <p className="screen-error" role="alert">
          {(query.error as unknown as ApiError).status === 404
            ? `No upstream MCP connector with slug '${slug}'.`
            : 'Failed to load the upstream.'}
        </p>
      )}
      {sync.isError && (
        <p className="screen-error" role="alert">
          {titledErrorMessage(sync.error)}
        </p>
      )}
      {sync.isSuccess && sync.data && <SyncSummaryView s={sync.data} />}

      {u && (
        <>
          <dl className="scr-dl" aria-label="Upstream details">
            <dt>Connector</dt>
            <dd className="scr-mono">{u.connector_name}</dd>
            <dt>URL</dt>
            <dd className="scr-mono">{u.url}</dd>
            <dt>Transport</dt>
            <dd>{u.transport}</dd>
            <dt>Auth scheme</dt>
            <dd>
              {u.auth_scheme}
              {u.api_key_header ? ` (${u.api_key_header})` : ''}
            </dd>
            <dt>Tool prefix category</dt>
            <dd>{u.category || '—'}</dd>
            <dt>Server</dt>
            <dd>
              {u.server_info?.name ?? '—'} {u.server_info?.version ?? ''}{' '}
              {u.protocol_version ? `(MCP ${u.protocol_version})` : ''}
            </dd>
            <dt>Last sync</dt>
            <dd>
              {fmtTime(u.last_sync_at)}
              {u.last_sync_error && <span className="scr-err"> — {u.last_sync_error}</span>}
            </dd>
          </dl>

          <div className="scr-panel">
            <h3>Exposure</h3>
            <ExposureEditor
              connectorName={u.connector_name}
              label={u.display_name || u.connector_name}
              exposure={u.exposure}
              // Served on the upstream record itself; ExposureEditor refreshes it, the
              // upstream list and GET /admin/connectors after a save.
              categories={u.categories}
              tosNote={
                u.tos_reviewed_at
                  ? undefined
                  : 'No vendor ToS review recorded yet — going live will be refused (409) until one is.'
              }
            />
          </div>

          <ReviewPanel slug={slug} />
          <TosPanel key={`tos-${u.tos_url ?? ''}`} upstream={u} />
          <CredentialsPanel key={`cred-${u.auth_scheme}-${u.api_key_header ?? ''}`} upstream={u} />
          {u.auth_scheme === 'oauth2' && <OAuthPanel slug={slug} />}
        </>
      )}

      {confirmSync && (
        <ConfirmDialog
          label="Confirm sync"
          confirmText="Sync"
          onCancel={() => setConfirmSync(false)}
          onConfirm={() => {
            setConfirmSync(false)
            sync.mutate()
          }}
        >
          <p>
            Run tools/list on <strong>{slug}</strong> now? New tools land pending, changes are held,
            and tools the upstream no longer lists are deactivated immediately.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
